import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { AvTooltip, Loading, WorkflowType } from '@fabric-ui/components';
import { External, Zlogo as Logo, Maximize, Minimize, X } from '@fabric-ui/icons';
import { FailFull as Error, FailFull2 as ErrorFull, SuccessFull2 as Success, SuccessFull } from '@fabric-ui/icons/colorful';
import { flex, shadowPrimaryBottomMd } from '@fabric-ui/theme';
import { QueryKey } from '@fabric-ui/utils';
import { Box, Button, Collapse, LinearProgress } from '@mui/material';
import { Link } from '@utils/AvRouter';
import { entityViewConfig } from '@utils/entityViewConfig';
import { gql } from 'graphql-request';
import useHandleError from '../hooks/useHandleError';
import { APP_PATHS, PAGE_PATHS } from '../types';
import { useAvContext } from './AvContextProvider';
import { AvContextType } from './types';

const success2Icon = <SuccessFull style={{ width: 24, height: 24 }} />;
const error2Icon = <ErrorFull style={{ width: 24, height: 24 }} />;
const successIcon = <Success style={{ width: 24, height: 24 }} />;
const errorIcon = <Error style={{ width: 24, height: 24 }} />;

export const GET_PROCESSES = gql`
  query getWFStatus($wfId: String!, $runId: String, $returnStatusOnly: Boolean) {
    getWFStatus(wfId: $wfId, runId: $runId, returnStatusOnly: $returnStatusOnly)
  }
`;

export enum WorkflowStatus {
  UNSPECIFIED = 'UNSPECIFIED',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  TIMED_OUT = 'TIMED_OUT',
}

export enum MessageStatus {
  SUCCESS = 'success',
  ERROR = 'error',
  LOADING = 'loading',
  WARNING = 'warning',
}

export interface WorkflowStatusResponse {
  status: WorkflowStatus;
  failedKeys: string[];
  successKeys: string[];
  runningMessage: string;
  result: string[];
  totalItems: number;
  errorMessage: string;
  triggerIds: string[];
  workflowInternalGuid: string;
  warningMessage: string;
  progressPercentage?: number;
}

type RunsPathType = (param: {
  getPathName: AvContextType['getPathName'];
  result?: string[];
  triggerIds?: string[];
  failedKeys?: string[];
}) => {
  to?: string;
  search?: Record<string, unknown>;
};

type WFTextType = {
  num?: number;
  message?: string;
  entityName?: string;
  result?: any;
};

const wfTexts: {
  [key in WorkflowType]: {
    success: ({ num, entityName, result }: WFTextType) => React.ReactNode;
    error: ({ num, entityName }: WFTextType) => React.ReactNode;
    warning?: ({ num, entityName }: WFTextType) => React.ReactNode;
    loading: ({ num, message, entityName }: WFTextType) => React.ReactNode;
    getHeader?: ({ num, entityName, result }: WFTextType) => React.ReactNode;
    action?: MessageAction;
  };
} = {
  [WorkflowType.BulkUpdateTickets]: {
    success: ({ num }) => `${num} Tickets Updated`,
    error: ({ num }) => `Failed to Update ${num} Tickets`,
    loading: () => 'Updating Tickets...',
  },
  [WorkflowType.ManualOverride]: {
    success: ({ num }) => `${num} Entities Updated`,
    error: ({ num }) => `Failed to Update ${num} Entities`,
    loading: () => 'Processing Entities...',
  },
  [WorkflowType.BulkCreateIssues]: {
    success: ({ num }) => `${num} Issues Created`,
    error: ({ num }) => `Failed to Create Issues for ${num} Tickets`,
    loading: () => 'Creating Issues...',
    action: {
      runsPath: ({ getPathName, triggerIds, failedKeys }) => ({
        to: getPathName(`${PAGE_PATHS.TARGETS}/activityLog/`, triggerIds?.join(','), `${APP_PATHS.PLATFORM}`).to,
        search:
          failedKeys && failedKeys?.length > 0
            ? { ENTITY_KEY: encodeURIComponent(JSON.stringify({ and: failedKeys.map(key => ({ operator: 'equals', value: key })) })) }
            : undefined,
      }),
    },
  },
  [WorkflowType.SplitTicket]: {
    success: ({ num, entityName }) => `${num} ${entityName}s Split`,
    error: ({ num, entityName }) => `Failed to Split ${num} ${entityName}s`,
    loading: ({ entityName }) => `Splitting ${entityName}s...`,
    action: {
      text: () => 'View Ticket',
      pathName: projectionName => {
        const pathMap: Record<string, string> = {
          [entityViewConfig.PolicyViolation.projectionName]: PAGE_PATHS.VIOLATION_TICKETS,
          [entityViewConfig.AlertNew.projectionName]: PAGE_PATHS.INCIDENTS,
        };
        return pathMap[projectionName] || PAGE_PATHS.TICKETS;
      },
      dynamicSearchKey: projectionName => {
        const searchKeyMap: Record<string, string> = {
          [entityViewConfig.ViolationTicket.projectionName]: 'violation_ticket._key',
          [entityViewConfig.AlertNew.projectionName]: 'incident._key',
        };
        return searchKeyMap[projectionName] || 'ticket._key';
      },
    },
  },
  [WorkflowType.MergeTickets]: {
    success: ({ num, entityName }) => `${num} ${entityName || 'Ticket'}s Merged`,
    error: ({ num, entityName }) => `Failed to Merge ${num} ${entityName || 'Ticket'}s`,
    loading: ({ entityName }) => `Merging ${entityName || 'Ticket'}s...`,
    action: {
      text: () => 'View Ticket',
      pathName: projectionName => {
        const pathMap: Record<string, string> = {
          [entityViewConfig.ViolationTicket.projectionName]: PAGE_PATHS.VIOLATION_TICKETS,
          [entityViewConfig.IncidentThreats.projectionName]: PAGE_PATHS.INCIDENTS,
        };
        return pathMap[projectionName] || PAGE_PATHS.TICKETS;
      },
    },
  },
  [WorkflowType.ManualGrouping]: {
    success: ({ num, entityName }) => `From ${num} ${entityName}s`,
    error: ({ num, entityName }) => `Failed to Merge ${num} ${entityName}s`,
    loading: ({ entityName }) => `Merging ${entityName}s...`,
    getHeader: ({ entityName, result }) => `${entityName}${result ? ` ${result[0]}` : ''} has been created successfully `,
    action: { searchKey: 'ticket._key' },
  },
  [WorkflowType.ManualCreateIncident]: {
    success: () => 'Incident Created',
    error: () => 'Failed to Create Incident',
    loading: () => 'Creating Incident...',
    action: {
      urlSearchParam: { ticketCategory: entityViewConfig.IncidentThreats.app },
      searchKey: 'incident._key',
      text: () => 'View Incident',
      pathName: () => PAGE_PATHS.INCIDENTS,
    },
  },
  [WorkflowType.ReprocessAggProj]: {
    success: () => 'Entities Processed Successfully',
    error: () => 'Processing Failed',
    loading: () => 'Processing Entities...',
    action: {
      runsPath: ({ getPathName, result }) => ({
        to: getPathName(`${PAGE_PATHS.MODEL_MANAGEMENT}/runs`, undefined, `${APP_PATHS.PLATFORM}`).to,
        search: result ? { guidId: result[0] } : undefined,
      }),
    },
  },
  [WorkflowType.IntegrationAttachment]: {
    success: () => 'File Attached Successfully',
    error: () => 'Failed to Attach File',
    loading: ({ message }) => message,
  },
  [WorkflowType.DataDeletion]: {
    success: () => 'Deleted Successfully',
    error: () => 'Failed to Delete',
    loading: () => 'Deleting data...',
  },
  [WorkflowType.AccountProvisioning]: {
    success: () => 'Successfully Provisioned Account',
    error: () => 'Account Provisioning Failed',
    loading: () => 'Loading Account Provisioning...',
  },
  [WorkflowType.AccountDeprovisioning]: {
    success: () => 'Successfully Deprovisioned Account',
    error: () => 'Account was Partially Deprovisioned',
    loading: () => 'Loading Account Deprovisioning...',
  },
  [WorkflowType.ExportDashboard]: {
    success: () => 'Export completed.',
    error: () => 'An error occurred. Please try again later',
    loading: () => 'Exporting dashboard. Please wait.',
  },
  [WorkflowType.ExportQuery]: {
    success: () => 'Export completed.',
    error: () => 'An error occurred.',
    loading: () => 'Export in progress. Please wait.',
    warning: () => 'Export completed.',
  },
  [WorkflowType.Default]: {
    success: num => `${num} Completed Successfully`,
    error: num => `${num || ''} Failed`,
    loading: () => 'Processing...',
  },
  [WorkflowType.RunFactorRules]: {
    success: () => `Factor Rules Run Completed Successfully`,
    error: () => `Factor Rules Run Failed`,
    loading: () => 'Processing...',
  },
  [WorkflowType.RunPolicyRules]: {
    success: () => `Policies Run Completed Successfully`,
    error: () => `Policies Run Failed`,
    loading: () => 'Processing...',
  },
  [WorkflowType.GlobalFactorsPublish]: {
    success: () => `Global Factors were Published Successfully`,
    error: () => `Global Factors Publishing Failed`,
    loading: () => 'Processing...',
  },
  [WorkflowType.DeletePolicyRules]: {
    success: () => `Policy has Been Successfully Deleted`,
    error: () => `Policy Deletion Failed`,
    loading: () => 'Loading Policy Deletion...',
  },
  [WorkflowType.DeleteFactorRules]: {
    success: () => `Factor has Been Successfully Deleted`,
    error: () => `Factor Deletion Failed`,
    loading: () => 'Loading Factor Deletion...',
  },
  [WorkflowType.DeleteUsers]: {
    success: () => 'Users Have Been Successfully Deleted',
    error: () => 'Error: one or more deletions failed. Please try again',
    loading: () => 'Loading Users Deletion...',
  },
  [WorkflowType.BulkCreateOutegration]: {
    success: ({ num }) => `${num || ''} Assets was added successfully to ServiceNow CMDB`,
    error: ({ num }) => `${num || ''} Assets failed to add to ServiceNow CMDB`,
    loading: ({ num }) => `Adding ${num || ''} assets to the CMDB`,
    action: {
      text: entityName => `View ${entityName}`,
      pathName: projectionName => {
        const pathMap: Record<string, string> = {
          [entityViewConfig.ViolationTicket.projectionName]: PAGE_PATHS.VIOLATION_TICKETS,
        };
        return pathMap[projectionName] || PAGE_PATHS.TICKETS;
      },
    },
  },
  [WorkflowType.UpdateAndProcessScoring]: {
    success: () => 'Updated and processed alerts scores successfully',
    error: () => 'Failed to update and process alerts score ',
    loading: () => 'Updating score and processing alerts...',
  },
  [WorkflowType.SaveCopilotSummaryToFixes]: {
    success: () => 'AI insights saved to Fixes tab successfully',
    error: () => 'Failed to save AI insights to Fixes tab',
    loading: () => 'Saving AI insights to Fixes tab',
  },
};
const getMessage = ({
  runId,
  wfType = WorkflowType.Default,
  type,
  total,
  result,
  errorMessage,
  runningMessage,
  entityName,
  aggregatedEntityName,
  triggerIds,
  failedKeys,
  projectionName,
  shouldBlock,
  progressPercentage,
}: {
  runId: string | null;
  wfType?: WorkflowType;
  type: MessageStatus;
  total?: number;
  result?: any[];
  errorMessage?: string;
  runningMessage?: string;
  entityName?: string;
  aggregatedEntityName?: string;
  triggerIds?: string[];
  failedKeys?: string[];
  projectionName?: string;
  shouldBlock?: boolean;
  progressPercentage?: number;
}): MessageListItem | null => {
  const process = wfTexts[wfType];
  const messageFn = process[type as 'success' | 'error' | 'loading' | 'warning'];

  if (!messageFn) {
    return null;
  }

  const message = messageFn({ num: total, message: runningMessage, entityName, result });
  const header = process.getHeader?.({ num: total, entityName: aggregatedEntityName, result }) || message;

  return {
    id: `${runId}_${type}`,
    type,
    header: type !== 'loading' && header,
    message,
    errorMessage,
    projectionName,
    progressPercentage,
    shouldBlock,
    action: process.action && { text: () => 'View Ticket', result, triggerIds, failedKeys, ...process.action },
  };
};

interface MessageAction {
  text?: (entityName?: string) => string;
  result?: any[];
  triggerIds?: string[];
  failedKeys?: string[];
  urlSearchParam?: Record<string, any>;
  searchKey?: string;
  dynamicSearchKey?: (projectionName: string) => string;
  runsPath?: RunsPathType;
  pathName?: (projectionName: string) => string;
}

export interface MessageListItem {
  id: string;
  type: MessageStatus;
  header: React.ReactNode | false;
  message: React.ReactNode;
  errorMessage?: string;
  projectionName?: string;
  entityName?: string;
  shouldBlock?: boolean;
  progressPercentage?: number;
  action?: MessageAction;
}

interface WorkflowStatusPollerProps {
  runInfo: RunInfo;
  setRunInfo: (runInfo: RunInfo, shouldDelete?: boolean) => void;
  onMessageUpdate: (updater: (prevMessages: MessageListItem[]) => MessageListItem[]) => void;
}

const WorkflowStatusPoller: React.FC<WorkflowStatusPollerProps> = ({ runInfo, setRunInfo, onMessageUpdate }) => {
  const {
    accountEntities: { aggProjs },
  } = useAvContext();
  const handleError = useHandleError();
  const { runId, wfId, returnStatusOnly, workflowType, projectionName, shouldBlock, onSuccess } = runInfo;

  useGqlQuery<WorkflowStatusResponse>({
    queryKey: [QueryKey.ProcessStatus, runId],
    query: GET_PROCESSES,
    variables: { runId, wfId, returnStatusOnly },
    onSuccess: data => {
      const {
        failedKeys,
        status,
        successKeys,
        runningMessage,
        result,
        totalItems,
        errorMessage,
        triggerIds,
        progressPercentage,
        workflowInternalGuid,
        warningMessage,
      } = data;

      const wfType = workflowType || WorkflowType.Default;
      const projName = projectionName || 'tickets';
      const entityName = aggProjs[projName]?.projDisplayName;
      const aggregatedEntity = Object.values(aggProjs).find(proj => proj.aggregates?.name === projName);
      const isDone = [WorkflowStatus.COMPLETED, WorkflowStatus.FAILED].includes(status);

      const commonProps = {
        runId,
        wfType,
        projectionName: projName,
        entityName,
        shouldBlock: status === WorkflowStatus.RUNNING && shouldBlock,
      };

      const newMessages = [
        isDone && (successKeys.length || result.length)
          ? getMessage({
              ...commonProps,
              type: MessageStatus.SUCCESS,
              total: successKeys.length,
              result,
              aggregatedEntityName: aggregatedEntity?.projDisplayName,
            })
          : undefined,
        isDone && failedKeys.length
          ? getMessage({
              ...commonProps,
              type: MessageStatus.ERROR,
              total: failedKeys.length,
              triggerIds,
              failedKeys,
              errorMessage,
              aggregatedEntityName: aggregatedEntity?.projDisplayName,
            })
          : undefined,
        status === WorkflowStatus.FAILED && !failedKeys.length
          ? getMessage({
              ...commonProps,
              type: MessageStatus.ERROR,
              total: totalItems,
              result: [workflowInternalGuid] as any,
              errorMessage,
              aggregatedEntityName: aggregatedEntity?.projDisplayName,
            })
          : undefined,
        status === WorkflowStatus.COMPLETED && !(successKeys.length || result.length)
          ? getMessage({
              ...commonProps,
              type: MessageStatus.SUCCESS,
              total: totalItems || 0,
              aggregatedEntityName: aggregatedEntity?.projDisplayName,
            })
          : undefined,
        status === WorkflowStatus.COMPLETED && warningMessage
          ? getMessage({
              ...commonProps,
              type: MessageStatus.WARNING,
              total: totalItems || 0,
              aggregatedEntityName: aggregatedEntity?.projDisplayName,
              errorMessage: warningMessage,
            })
          : undefined,
        status === WorkflowStatus.RUNNING
          ? getMessage({
              ...commonProps,
              type: MessageStatus.LOADING,
              progressPercentage,
              total: totalItems,
              runningMessage,
              aggregatedEntityName: aggregatedEntity?.projDisplayName,
            })
          : undefined,
      ].filter((d): d is MessageListItem => !!d);

      if (newMessages.length > 0) {
        onMessageUpdate(prev => [...newMessages, ...prev.filter(({ id }) => !id.startsWith(runId))]);
      }

      if (isDone) {
        setRunInfo(runInfo, true);
        onSuccess?.({ result, successKeys, status });
      }

      if (status === WorkflowStatus.UNSPECIFIED) {
        setRunInfo(runInfo, true);
        handleError(`${runId} returned UNSPECIFIED status`);
      }
    },
    refetchInterval: 3000,
    gcTime: 0,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  return null;
};

export interface AvSnackBarProps {
  runInfo: RunInfo[];
  setRunInfo: (runInfo: RunInfo, shouldDelete?: boolean) => void;
  instantMessages: MessageListItem[];
  setInstantMessages: React.Dispatch<React.SetStateAction<MessageListItem[]>>;
  messageList: MessageListItem[];
  setMessageList: React.Dispatch<React.SetStateAction<MessageListItem[]>>;
}

export const AvSnackBar: React.FC<AvSnackBarProps> = ({
  runInfo,
  setRunInfo,
  instantMessages,
  setInstantMessages,
  messageList,
  setMessageList,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [dismissedRuns, setDismissedRuns] = useState<Record<string, boolean>>({});
  const maskScreen = useMemo(() => messageList.some(({ shouldBlock }) => shouldBlock), [messageList]);

  const dismissRun = (id: string) => {
    const [runId] = id.split('_');
    setDismissedRuns(prev => ({ ...prev, [runId]: true }));
    setMessageList(prev => prev.filter(({ id: mid }) => mid !== id));
  };

  const dismissAll = () => {
    const runToDismiss = runInfo.reduce(
      (acc, { runId }) => {
        acc[runId] = true;
        return acc;
      },
      {} as Record<string, boolean>
    );
    setDismissedRuns(prev => ({ ...prev, ...runToDismiss }));
    setMessageList([]);
  };

  useEffect(() => {
    if (instantMessages?.length) {
      setMessageList(prev => [...prev, ...instantMessages].filter(Boolean));
      setInstantMessages([]);
    }
  }, [instantMessages, setInstantMessages, setMessageList]);

  const hasLoading = useMemo(() => messageList.some(m => m.type === MessageStatus.LOADING), [messageList]);

  return (
    <>
      {runInfo
        .filter(({ runId }) => !dismissedRuns[runId])
        .map(ri => (
          <WorkflowStatusPoller key={ri.runId} runInfo={ri} setRunInfo={setRunInfo} onMessageUpdate={setMessageList} />
        ))}
      {messageList.length > 0 ? (
        <>
          {maskScreen && (
            <Box
              sx={theme => ({
                backgroundColor: 'var(--color-content-base-primary)',
                opacity: 0.6,
                position: 'absolute',
                height: '100%',
                width: '100%',
                zIndex: theme.zIndex.snackbar,
              })}
            />
          )}
          <Box
            sx={{ position: 'absolute', bottom: 90, right: 40, width: 400, zIndex: theme => theme.zIndex.snackbar + 1 }}
            onClick={e => e.stopPropagation()}>
            <Box
              sx={{
                ...flex.itemsCenter,
                gap: 1,
                height: 32,
                backgroundColor: 'var(--color-surface-immutable-darkGrey)',
                px: 1,
                py: 1,
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                borderBottomLeftRadius: collapsed ? 12 : undefined,
                borderBottomRightRadius: collapsed ? 12 : undefined,
                boxShadow: shadowPrimaryBottomMd,
                '&, .MuiButton-root': {
                  fontSize: 12,
                  color: 'var(--color-content-immutable-white)',
                },
                '.MuiButton-root:hover': {
                  color: 'var(--color-content-inverted-base-tertiary)',
                },
                '> div:first-of-type': {
                  height: 20,
                  marginLeft: '4px',
                },
              }}>
              {hasLoading ? (
                <Loading height={20} />
              ) : messageList.filter(({ type }) => type === MessageStatus.ERROR).length === 0 ? (
                successIcon
              ) : messageList.filter(({ type }) => type === MessageStatus.SUCCESS).length === 0 ? (
                errorIcon
              ) : (
                <Logo style={{ marginLeft: '4px' }} />
              )}
              <span style={{ flexGrow: 1 }}>
                {messageList.filter(({ type }) => type !== MessageStatus.LOADING).length}/{messageList.length} Processes Done
              </span>
              {!maskScreen && (
                <Button onClick={dismissAll} sx={{ px: 1 }}>
                  Dismiss
                </Button>
              )}
              <AvTooltip title={collapsed ? 'Expand' : 'Collapse'}>
                <Button onClick={() => setCollapsed(!collapsed)}>{collapsed ? <Maximize /> : <Minimize />}</Button>
              </AvTooltip>
            </Box>
            <Collapse in={!collapsed} timeout="auto" unmountOnExit>
              {messageList.map(
                ({ id, type, message, errorMessage, header, action, projectionName, entityName, shouldBlock, progressPercentage }) => {
                  const Component = alertTypes[type];
                  return (
                    <Box
                      key={id}
                      sx={{
                        ...flex.itemsStart,
                        gap: 2,
                        borderRadius: '8px',
                        boxShadow: shadowPrimaryBottomMd,
                        backgroundColor: 'var(--color-surface-elevated-20)',
                        py: '12px',
                        pl: 1,
                        pr: 2,
                        my: 1,
                      }}>
                      <Component
                        message={message}
                        header={header}
                        failed={type === MessageStatus.ERROR || type === MessageStatus.WARNING}
                        errorMessage={errorMessage}
                        action={action}
                        projectionName={projectionName}
                        entityName={entityName}
                        progressPercentage={progressPercentage}
                      />
                      {!shouldBlock && (
                        <Button onClick={() => dismissRun(id)}>
                          <X />
                        </Button>
                      )}
                    </Box>
                  );
                }
              )}
            </Collapse>
          </Box>
        </>
      ) : null}
    </>
  );
};

interface LoadingAlertProps {
  message: React.ReactNode;
  progressPercentage?: number;
}

const LoadingAlert = ({ message, progressPercentage }: LoadingAlertProps) => (
  <Box sx={{ ...flex.col, gap: 2, width: '100%', fontSize: 12, paddingLeft: 1 }}>
    {message}
    <LinearProgress
      value={(progressPercentage ?? 0) * 100}
      sx={{
        height: '6px',
        borderRadius: '3px',
        '& .MuiLinearProgress-bar': {
          borderRadius: '3px',
        },
      }}
      variant={progressPercentage !== undefined ? 'determinate' : 'indeterminate'}
    />
  </Box>
);

interface StatusAlertProps {
  message: React.ReactNode;
  header: React.ReactNode;
  failed: boolean;
  errorMessage?: string;
  projectionName?: string;
  entityName?: string;
  action?: MessageAction;
}

function StatusAlert({ message, header, errorMessage, failed, projectionName, entityName, action }: StatusAlertProps) {
  const {
    getPathName,
    accountEntities: { aggProjs },
  } = useAvContext();
  const {
    result,
    text,
    urlSearchParam,
    runsPath,
    searchKey,
    dynamicSearchKey,
    triggerIds,
    failedKeys,
    pathName = () => PAGE_PATHS.TICKETS,
  } = action || {};

  return (
    <Box sx={{ ...flex.itemsStart, gap: 1, width: '100%' }}>
      {failed ? error2Icon : success2Icon}
      <Box sx={{ ...flex.col }}>
        <Box sx={{ color: 'var(--color-content-base-primary)', fontWeight: 600 }}>{header}</Box>
        <Box sx={{ fontSize: 12, mt: '1px', mb: '5px', color: 'var(--color-content-base-secondary)' }}>{errorMessage || message}</Box>
        {action && (
          <>
            <Box sx={{ ...flex.row, gap: 1 }}>
              {failed && runsPath && (result || failedKeys) && (
                <Button
                  sx={{ alignSelf: 'flex-start', pl: 0, color: 'var(--color-content-interactive-primary-default)' }}
                  component={Link}
                  size="xSmall"
                  {...runsPath({ getPathName, result, triggerIds, failedKeys })}>
                  Show Logs
                </Button>
              )}
            </Box>
            <Box sx={{ ...flex.row, gap: 2 }}>
              {!failed && result && projectionName && pathName ? (
                <Button
                  sx={{ color: 'var(--color-content-interactive-primary-default)', fontSize: 12, gap: '4px' }}
                  component={Link}
                  target="_blank"
                  to={`${getPathName(pathName(projectionName)).to}/${result.length === 1 ? result[0] : ''}`}
                  search={{
                    urlSearchParam,
                    [`${searchKey || dynamicSearchKey?.(projectionName) || `${aggProjs[projectionName]?.pathAlias || 'ticket'}._key`}`]:
                      result,
                  }}>
                  {text?.(entityName)}
                  {result.length > 1 ? 's' : ''}
                  {!!text?.(entityName) && <External style={{ width: 16, height: 16 }} />}
                </Button>
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

const alertTypes = {
  [MessageStatus.LOADING]: LoadingAlert,
  [MessageStatus.SUCCESS]: StatusAlert,
  [MessageStatus.ERROR]: StatusAlert,
  [MessageStatus.WARNING]: StatusAlert,
};

export interface RunInfo {
  runId: string;
  wfId: string;
  returnStatusOnly?: boolean;
  workflowType?: WorkflowType;
  projectionName?: string;
  onSuccess?: (arg: { result: any; successKeys: any; status: any }) => unknown;
  shouldBlock?: boolean;
  progressPercentage?: number;
}

export type NotificationContextType = Omit<AvSnackBarProps, 'children'>;
export const NotificationContext = React.createContext<NotificationContextType>({} as any);

export default function AvSnackBarProvider({ children }: React.PropsWithChildren = {}) {
  const {
    accountEntities: { aggProjs },
  } = useAvContext();
  const [runInfo, setRunInfoDirect] = useState<RunInfo[]>([]);
  const [instantMessages, setInstantMessages] = useState<MessageListItem[]>([]);
  const [messageList, setMessageList] = useState<MessageListItem[]>([]);

  const setRunInfo = useCallback(
    (info: RunInfo, shouldDelete?: boolean) => {
      if (shouldDelete) {
        setRunInfoDirect(prev => prev.filter(item => item.runId !== info.runId));
      } else {
        setRunInfoDirect(prev => [...prev, info]);
        const { runId, workflowType, projectionName, shouldBlock, progressPercentage } = info;
        const wfType = workflowType || WorkflowType.Default;
        const projName = projectionName || 'tickets';
        const entityName = aggProjs[projName]?.projDisplayName;
        const aggregatedEntity = Object.values(aggProjs).find(proj => proj.aggregates?.name === projName);

        const loadingMessage = getMessage({
          runId,
          wfType,
          projectionName: projName,
          entityName,
          type: MessageStatus.LOADING,
          progressPercentage,
          aggregatedEntityName: aggregatedEntity?.projDisplayName,
          shouldBlock,
        });

        if (loadingMessage) {
          setMessageList(prev => (prev.some(m => m.id.startsWith(runId)) ? prev : [...prev, loadingMessage]));
        }
      }
    },
    [aggProjs]
  );

  const value = useMemo(
    () => ({ runInfo, setRunInfo, instantMessages, setInstantMessages, messageList, setMessageList }),
    [runInfo, instantMessages, setRunInfo, messageList, setMessageList]
  );

  return (
    <NotificationContext value={value}>
      {children}
      <AvSnackBar
        runInfo={runInfo}
        setRunInfo={setRunInfo}
        instantMessages={instantMessages}
        setInstantMessages={setInstantMessages}
        messageList={messageList}
        setMessageList={setMessageList}
      />
    </NotificationContext>
  );
}
